/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import AccordionCustom from "../../legacy/Accordion";
import { connect, useDispatch } from "react-redux";
import { getStoragePatient, getUserData } from "../../../utils/storageUtils";
import { createConsultation } from "../../../redux/consultations/actions";
import { Combobox } from "@/components/ui/combobox";
import { useQueryClient } from "react-query";
import AppointmentSlotPicker from "../../Availability/AppointmentSlotPicker";
import { DOCTOR_SLOTS_KEY } from "hooks/queries/useDoctorAvailability";
import { addDays } from "utils/availability";
import { todayIsoDate } from "utils/age";
import { isDoctor, isAdmin } from "../../../constants/roles";
import { eyeDistance, eyeTypeName, visionRequests } from "../consultationModel";
import { medecinTypeOptions, timeOfDayOptions } from "../utils";
import { cn } from "@/lib/utils";
import { inputClass } from "@/lib/formStyles";
import { openProtectedFile } from "../../../utils/openProtectedFile";
import useActsLookup from "hooks/queries/useActsLookup";
import useDrugsLookupSearch from "hooks/queries/useDrugsLookupSearch";

const storagePatient = getStoragePatient();

/**
 * Combobox d'un médicament en mode async — chaque ligne du formulaire de
 * prescription a son propre query state. Extrait en sous-composant pour
 * que `useDrugsLookupSearch` puisse être appelé par instance (un hook ne
 * peut pas être appelé dans une `.map()`).
 */
function DrugCombobox({ value, onChange, className }) {
  const [q, setQ] = useState("");
  const { data: drugs = [], isFetching } = useDrugsLookupSearch(q);
  const options = drugs.map((d) => ({ value: d.id, label: d.label }));
  const trimmed = q.trim();
  return (
    <Combobox
      options={options}
      value={value ?? null}
      placeholder="Médicament"
      searchPlaceholder="Rechercher un médicament..."
      onSearchChange={setQ}
      loading={trimmed.length >= 2 && isFetching}
      emptyText={
        trimmed.length < 2 ? "Tapez au moins 2 caractères" : "Aucun résultat"
      }
      onChange={onChange}
      className={className}
    />
  );
}

// Variante de `inputClass` pour cellules de tableau : pas de `mt-1` (pas de
// label `<span>` au-dessus, on est dans une <td>) — twMerge dédoublonne.
const inputCellClass = cn(inputClass, "mt-0");

// Le back renvoie `createdAt` / `appointmentDate` au format ISO complet
// (ex. "2026-05-06T17:49:17.770094"). On n'affiche que la date — l'heure
// du timestamp serveur n'a pas d'intérêt clinique, et le format brut
// alourdit l'UI. Renvoie "—" si la valeur est falsy.
const formatDateOnly = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

// Champ borderless pour la grille vision (sphère/cylindre/axe) : on veut
// que la cellule entière paraisse cliquable, donc pas de border ni de bg
// — c'est la `<td>` qui porte la border.
const visionCellClass =
  "h-full w-full text-slate-700 px-2 py-0 text-center text-sm bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 rounded";

// Label FR pour les types de vision retournés par le back.
const VISION_TYPE_LABELS = {
  Distance_vision: "Vision de loin",
  All_distance_vision: "Vision toute distance",
  Near_vision: "Vision de près",
};

// Type de médicament (DrugsTypeEnum côté back) → label FR. Aligné sur
// `medecinTypeOptions` côté formulaire.
const DRUG_TYPE_LABELS = {
  EYEDROPS: "Collyre",
  TABLETS: "Comprimé",
  SYRUP: "Sirop",
  CREAM: "Pommade",
  CAPSULES: "Gélule",
};

// Regroupe `glassesPrescription.visionList` (plat) en
// `{ Distance_vision: {EYE_RIGHT, EYE_LEFT}, ... }` pour l'affichage en
// grille OD/OG. Tolère un visionType absent ou un seul œil renseigné.
const groupVisionByType = (visionList = []) => {
  const grouped = {};
  for (const v of visionList || []) {
    const t = v?.visionType;
    if (!t) continue;
    if (!grouped[t]) grouped[t] = {};
    grouped[t][v.eyeType] = v;
  }
  return grouped;
};

// Une ligne de la grille vision est "non vide" si au moins un de ses six
// champs (sphère/cylindre/axe × OD/OG) est renseigné. Évite d'afficher
// des rangées creuses pour des visionTypes que le médecin n'a pas saisis.
const isVisionRowFilled = (row) => {
  if (!row) return false;
  const eyes = [row.EYE_RIGHT, row.EYE_LEFT];
  return eyes.some(
    (eye) =>
      (eye?.sphere ?? "") !== "" ||
      (eye?.cylinder ?? "") !== "" ||
      (eye?.axis ?? "") !== ""
  );
};

const TabTwo = ({
  current_patient: curr_patient,
  consultations,
  creating_consultation,
  errors,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  // Acts : petite table cachée 30 min côté client (cf. useActsLookup).
  // Drugs : autocomplete async par ligne de prescription (cf. DrugCombobox).
  const { data: acts = [] } = useActsLookup();
  const [openForm, setOpenForm] = useState(false);
  // Enregistrement en cours : le formulaire ne se ferme qu'en cas de succès.
  const [isSaving, setIsSaving] = useState(false);
  const current_patient = Object.keys(curr_patient).length
    ? curr_patient
    : storagePatient;
  const currentUser = getUserData();

  const [form, setForm] = useState({
    actsDiagnosticRequests: [
      { id: 0, actsId: null, eyeTypeName: "", result: "", resultFile: "" },
    ],
    dosageRequests: [
      {
        id: 0,
        drugsId: null,
        frequency: "",
        quantity: 0,
        drugsType: "",
        duration: "",
        timeOfDays: "",
      },
    ],
    doctorId: current_patient?.doctor?.id,
    patientId: current_patient?.id,
    appointmentTime: "08:00",
    appointmentDate: addDays(todayIsoDate(), 1),
    prismRequest: {
      mineral: false,
      organic: false,
      transition: false,
      transitionAntiBlueLight: false,
      transitionAr: false,
    },
    visionRequests,
    biTemporalRequest: {
      branchLength: "",
      doubleHearths: false,
      interpupillaryDistance: "",
      progressive: false,
      shadePbx: false,
      shadePgx: false,
      shadeWhite: false,
    },
  });

  // Resync patientId / doctorId when the active patient changes — Tabs
  // stay mounted across patient switches (Radix), so the captured-at-init
  // values would otherwise post the consultation against the previous
  // patient. Mirrors the same pattern in TabThree.
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      patientId: current_patient?.id,
      doctorId: current_patient?.doctor?.id,
    }));
  }, [current_patient?.id, current_patient?.doctor?.id]);

  const exam = {
    id: 0,
    actsId: null,
    eyeTypeName: "",
    result: "",
    resultFile: "",
  };

  const drug = {
    id: 0,
    drugsId: null,
    frequency: "",
    quantity: 0,
    drugsType: "",
    duration: "",
    timeOfDays: "",
  };

  const generateId = (data) =>
    data.length === 0 ? 0 : Number(data[data.length - 1].id) + 1;

  const handleTimeChange = (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, appointmentTime: value }));
  };

  // Créneau libre choisi (la date change quand on saute au prochain créneau libre).
  const handleSlotSelect = (appointmentDate, appointmentTime) => {
    setForm((prev) => ({ ...prev, appointmentDate, appointmentTime }));
  };

  // Normalise un appointmentTime renvoyé par le back en chaîne HH:mm.
  // Gère trois formats : LocalTime sérialisé en objet {hour, minute},
  // chaîne "HH:mm:ss", ou chaîne "HH:mm".
  const normalizeTimeString = (timeStr) => {
    if (!timeStr) return null;
    if (typeof timeStr === "object" && timeStr.hour !== undefined) {
      const hour = String(timeStr.hour).padStart(2, "0");
      const minute = String(timeStr.minute || 0).padStart(2, "0");
      return `${hour}:${minute}`;
    }
    if (typeof timeStr === "string") return timeStr.substring(0, 5);
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Met à jour une ligne sans muter la référence existante : on construit
  // un nouveau tableau via map. L'ancienne version mutait `data` en place
  // — invariant React rompu si la même référence persistait dans l'état.
  const handleArrayChange = (e, { id, name }) => {
    setForm((prev) => {
      const list = prev[name] || [];
      const next = list.map((row) =>
        row.id === id ? { ...row, [e.target.name]: e.target.value } : row
      );
      return { ...prev, [name]: next };
    });
  };

  const handleAddDrugs = (e) => {
    e.preventDefault();
    setForm((prev) => ({
      ...prev,
      dosageRequests: [
        ...prev.dosageRequests,
        { ...drug, id: generateId(prev.dosageRequests) },
      ],
    }));
  };

  const handleRemoveDrug = (e, idx) => {
    e.preventDefault();
    setForm((prev) => ({
      ...prev,
      dosageRequests: prev.dosageRequests.filter((i) => i.id !== idx),
    }));
  };

  const handleAddActs = (e) => {
    e.preventDefault();
    setForm((prev) => ({
      ...prev,
      actsDiagnosticRequests: [
        ...prev.actsDiagnosticRequests,
        { ...exam, id: generateId(prev.actsDiagnosticRequests) },
      ],
    }));
  };

  const handleRemoveAct = (e, idx) => {
    e.preventDefault();
    setForm((prev) => ({
      ...prev,
      actsDiagnosticRequests: prev.actsDiagnosticRequests.filter(
        (i) => i.id !== idx
      ),
    }));
  };

  // Fin de l'enregistrement : succès → fermeture ; échec (dont le 409
  // `error.availability.slot_unavailable`, déjà toasté par apiRequest) → le
  // formulaire reste ouvert et les créneaux sont rechargés.
  useEffect(() => {
    if (!isSaving || creating_consultation) return;
    setIsSaving(false);
    if (errors?.length) {
      queryClient.invalidateQueries([DOCTOR_SLOTS_KEY]);
      return;
    }
    setOpenForm(false);
  }, [isSaving, creating_consultation, errors, queryClient]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSaving(true);
    dispatch(
      createConsultation({
        ...form,
        visionRequests: [...allDistanceVision, ...distanceVision, ...nearVision],
      })
    );
  };

  const adaptSelectActsOptions = (data) =>
    (data || []).map((item) => ({ value: item?.id, label: item?.label }));

  const [allDistanceVision, setAllDistanceVision] = useState([
    {
      axis: "",
      cylinder: "",
      eyeTypeName: eyeTypeName.EYE_RIGHT,
      sphere: "",
      visionType: eyeDistance.All_distance_vision,
    },
    {
      axis: "",
      cylinder: "",
      eyeTypeName: eyeTypeName.EYE_LEFT,
      sphere: "",
      visionType: eyeDistance.All_distance_vision,
    },
  ]);

  const [distanceVision, setDistanceVision] = useState([
    {
      axis: "",
      cylinder: "",
      eyeTypeName: eyeTypeName.EYE_RIGHT,
      sphere: "",
      visionType: eyeDistance.Distance_vision,
    },
    {
      axis: "",
      cylinder: "",
      eyeTypeName: eyeTypeName.EYE_LEFT,
      sphere: "",
      visionType: eyeDistance.Distance_vision,
    },
  ]);

  const [nearVision, setNearVision] = useState([
    {
      axis: "",
      cylinder: "",
      eyeTypeName: eyeTypeName.EYE_RIGHT,
      sphere: "",
      visionType: eyeDistance.Near_vision,
    },
    {
      axis: "",
      cylinder: "",
      eyeTypeName: eyeTypeName.EYE_LEFT,
      sphere: "",
      visionType: eyeDistance.Near_vision,
    },
  ]);

  // Spread du tableau pour produire une nouvelle référence — sinon le
  // diff React peut rater l'update car la cellule contrôlée garde la
  // même address mémoire.
  const handleVisionChange = (e, idx, data, setData) => {
    const next = data.map((row, i) =>
      i === idx ? { ...row, [e.target.name]: e.target.value } : row
    );
    setData(next);
  };

  const handleNestedChange = (e, name, isBool = false) => {
    setForm((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        [e.target.name]: isBool ? e.target.checked : e.target.value,
      },
    }));
  };

  const checkboxClass =
    "rounded border-slate-300 text-emerald-600 shadow-sm focus:border-emerald-300 focus:ring focus:ring-offset-0 focus:ring-emerald-200 focus:ring-opacity-50";

  const sectionEyebrow = "text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold";

  const filteredConsultations = (consultations || [])
    .filter((item) => item?.patient?.id === current_patient?.id)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const isPractitioner =
    isDoctor(currentUser?.user) || isAdmin(currentUser?.user);

  return (
    <div className="space-y-6" data-tab="soins">
      {/* Action header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className={sectionEyebrow}>Parcours de soins</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-800">
            Consultations
          </h2>
          <p className="text-sm text-slate-500">
            {filteredConsultations.length} consultation
            {filteredConsultations.length > 1 ? "s" : ""} pour ce patient
          </p>
        </div>
        {!openForm && (
          <button
            type="button"
            onClick={() => setOpenForm(true)}
            className="group/btn relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
            />
            <i className="bx bx-plus text-lg" />
            Nouvelle consultation
          </button>
        )}
      </div>

      {/* Formulaire création */}
      {openForm && (
        <form className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm space-y-8">
          <div className="flex items-start justify-between">
            <div>
              <p className={sectionEyebrow}>Nouvelle consultation</p>
              <h3 className="mt-1 text-base font-semibold text-slate-800">
                Saisir les éléments de la séance
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setOpenForm(false)}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
              aria-label="Fermer"
            >
              <i className="bx bx-x text-xl" />
            </button>
          </div>

          {/* Motif */}
          <label className="block">
            <span className="block text-sm font-medium text-slate-700">
              Motif <span className="text-rose-600">*</span>
            </span>
            <textarea
              name="complaint"
              onChange={handleChange}
              rows={4}
              placeholder="Décrivez en détail la raison de la consultation : symptômes, durée, contexte…"
              className={cn(inputClass, "h-auto min-h-[112px] py-2.5 resize-y leading-relaxed")}
            />
          </label>

          {/* Examens */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-700">Examens</h4>
              <div className="hidden sm:block flex-1 mx-4 h-px bg-slate-100" />
              <button
                onClick={handleAddActs}
                type="button"
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50 hover:border-emerald-200"
              >
                <i className="bx bx-plus-circle text-base" />
                Ajouter
              </button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                    <th className="px-3 py-3 text-left">Acte</th>
                    <th className="px-3 py-3 text-left whitespace-nowrap">
                      Œil droit
                    </th>
                    <th className="px-3 py-3 text-left whitespace-nowrap">
                      Œil gauche
                    </th>
                    <th className="px-3 py-3 text-left">Fichier</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.actsDiagnosticRequests.map((item, idx) => (
                    <tr
                      key={item.id}
                      className="border-t border-slate-100 align-middle"
                    >
                      <td className="px-3 py-3 min-w-[260px]">
                        <Combobox
                          options={adaptSelectActsOptions(acts)}
                          value={item?.actsId ?? null}
                          placeholder="Acte"
                          searchPlaceholder="Rechercher un acte..."
                          className="w-full"
                          onChange={(val) => {
                            setForm((prev) => {
                              const next = [...prev.actsDiagnosticRequests];
                              next[idx] = { ...next[idx], actsId: val.value };
                              return { ...prev, actsDiagnosticRequests: next };
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          name="resultEyeRight"
                          onChange={(e) =>
                            handleArrayChange(e, {
                              id: item.id,
                              name: "actsDiagnosticRequests",
                            })
                          }
                          className={inputCellClass}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          name="resultEyeLeft"
                          onChange={(e) =>
                            handleArrayChange(e, {
                              id: item.id,
                              name: "actsDiagnosticRequests",
                            })
                          }
                          className={inputCellClass}
                        />
                      </td>
                      <td className="px-3 py-3 min-w-[200px]">
                        <input
                          type="file"
                          className="block w-full text-xs text-slate-500 file:text-xs file:font-semibold file:py-1.5 file:px-3 file:bg-emerald-50 file:text-emerald-700 file:rounded-full file:border-0 file:mr-3 hover:file:bg-emerald-100 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <button
                          onClick={(e) => handleRemoveAct(e, idx)}
                          type="button"
                          className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                          aria-label="Supprimer"
                        >
                          <i className="bx bx-trash text-lg" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Diagnostic */}
          <label className="block">
            <span className="block text-sm font-medium text-slate-700">
              Diagnostic <span className="text-rose-600">*</span>
            </span>
            <textarea
              name="diagnostic"
              onChange={handleChange}
              placeholder="Synthèse du diagnostic…"
              className={cn(inputClass, "h-32 py-2.5 resize-none")}
            />
          </label>

          {/* Ordonnance lunettes */}
          <section className="space-y-4">
            <div className="flex items-center">
              <h4 className="text-sm font-semibold text-slate-700">
                Ordonnance des lunettes
              </h4>
              <div className="flex-1 ml-4 h-px bg-slate-100" />
            </div>

            {/* Vision grid */}
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                    <th className="px-3 py-3 text-left"></th>
                    <th
                      scope="col"
                      colSpan={3}
                      className="px-3 py-3 text-center border-l border-slate-200"
                    >
                      OD
                    </th>
                    <th
                      scope="col"
                      colSpan={3}
                      className="px-3 py-3 text-center border-l border-slate-200"
                    >
                      OG
                    </th>
                  </tr>
                  <tr className="bg-white text-[11px] text-slate-500 font-medium border-t border-slate-200">
                    <th className="px-3 py-2 text-left"></th>
                    <td className="min-w-[100px] text-center px-2 py-2 border-l border-slate-200">
                      Sphère
                    </td>
                    <td className="min-w-[100px] text-center px-2 py-2">
                      Cylindre
                    </td>
                    <td className="min-w-[100px] text-center px-2 py-2">
                      Axe
                    </td>
                    <td className="min-w-[100px] text-center px-2 py-2 border-l border-slate-200">
                      Sphère
                    </td>
                    <td className="min-w-[100px] text-center px-2 py-2">
                      Cylindre
                    </td>
                    <td className="min-w-[100px] text-center px-2 py-2">
                      Axe
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      label: "Vision de loin",
                      data: distanceVision,
                      setData: setDistanceVision,
                    },
                    {
                      label: "Vision toute distance",
                      data: allDistanceVision,
                      setData: setAllDistanceVision,
                    },
                    {
                      label: "Vision de près",
                      data: nearVision,
                      setData: setNearVision,
                    },
                  ].map((row) => (
                    <tr
                      key={row.label}
                      className="border-t border-slate-200 hover:bg-slate-50/40 transition"
                    >
                      <th
                        scope="row"
                        className="h-14 px-3 py-2 text-left text-sm font-medium text-slate-700 whitespace-nowrap"
                      >
                        {row.label}
                      </th>
                      {[0, 1].flatMap((eyeIdx) =>
                        ["sphere", "cylinder", "axis"].map((field, fIdx) => (
                          <td
                            key={`${eyeIdx}-${field}`}
                            className={
                              "h-14 p-1 " +
                              (fIdx === 0 ? "border-l border-slate-200" : "")
                            }
                          >
                            <input
                              name={field}
                              type="text"
                              value={row.data[eyeIdx][field]}
                              onChange={(e) =>
                                handleVisionChange(
                                  e,
                                  eyeIdx,
                                  row.data,
                                  row.setData
                                )
                              }
                              className={visionCellClass}
                            />
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bi-temporal + Prism */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 p-4">
                <h5 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-4">
                  Bi-temporale
                </h5>
                <div className="space-y-3">
                  <label className="flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-600">
                      Longueur des branches
                    </span>
                    <input
                      name="branchLength"
                      type="text"
                      value={form.biTemporalRequest.branchLength}
                      onChange={(e) =>
                        handleNestedChange(e, "biTemporalRequest")
                      }
                      className={cn(inputClass, "mt-0 w-32 h-9")}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-600">
                      Distance interpupillaire
                    </span>
                    <input
                      name="interpupillaryDistance"
                      type="text"
                      value={form.biTemporalRequest.interpupillaryDistance}
                      onChange={(e) =>
                        handleNestedChange(e, "biTemporalRequest")
                      }
                      className={cn(inputClass, "mt-0 w-32 h-9")}
                    />
                  </label>
                  <div>
                    <span className="block text-sm text-slate-600 mb-2">
                      Teinte
                    </span>
                    <div className="flex items-center gap-4 pl-1">
                      {[
                        { name: "shadeWhite", label: "Blanc" },
                        { name: "shadePgx", label: "PGx" },
                        { name: "shadePbx", label: "PBx" },
                      ].map((opt) => (
                        <label
                          key={opt.name}
                          className="flex items-center gap-2"
                        >
                          <input
                            type="checkbox"
                            name={opt.name}
                            checked={form.biTemporalRequest[opt.name]}
                            onChange={(e) =>
                              handleNestedChange(e, "biTemporalRequest", true)
                            }
                            className={checkboxClass}
                          />
                          <span className="text-sm text-slate-600">
                            {opt.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
                    <span className="text-sm text-slate-600">
                      Double foyers
                    </span>
                    <input
                      type="checkbox"
                      name="doubleHearths"
                      checked={form.biTemporalRequest.doubleHearths}
                      onChange={(e) =>
                        handleNestedChange(e, "biTemporalRequest", true)
                      }
                      className={checkboxClass}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-600">Progressifs</span>
                    <input
                      type="checkbox"
                      name="progressive"
                      checked={form.biTemporalRequest.progressive}
                      onChange={(e) =>
                        handleNestedChange(e, "biTemporalRequest", true)
                      }
                      className={checkboxClass}
                    />
                  </label>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h5 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-4">
                  Prisme
                </h5>
                <div className="space-y-2.5">
                  {[
                    { name: "mineral", label: "Minéral" },
                    { name: "organic", label: "Organique" },
                    { name: "transition", label: "Transition" },
                    { name: "transitionAr", label: "Transition + AR" },
                    {
                      name: "transitionAntiBlueLight",
                      label: "Transition + Anti-lumière bleue",
                    },
                  ].map((opt) => (
                    <label
                      key={opt.name}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="text-sm text-slate-600">
                        {opt.label}
                      </span>
                      <input
                        type="checkbox"
                        name={opt.name}
                        checked={form.prismRequest[opt.name]}
                        onChange={(e) =>
                          handleNestedChange(e, "prismRequest", true)
                        }
                        className={checkboxClass}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Ordonnance médicale */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-700">
                Ordonnance médicale
              </h4>
              <div className="hidden sm:block flex-1 mx-4 h-px bg-slate-100" />
              <button
                type="button"
                onClick={handleAddDrugs}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50 hover:border-emerald-200"
              >
                <i className="bx bx-plus-circle text-base" />
                Ajouter
              </button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                    <th className="px-3 py-3 text-left">Médicament</th>
                    <th className="px-3 py-3 text-left whitespace-nowrap">
                      Quantité
                    </th>
                    <th className="px-3 py-3 text-left whitespace-nowrap">
                      Fréquence
                    </th>
                    <th className="px-3 py-3 text-left">Type</th>
                    <th className="px-3 py-3 text-left">Moment</th>
                    <th className="px-3 py-3 text-left">Durée</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.dosageRequests.map((item, idx) => (
                    <tr
                      key={item.id}
                      className="border-t border-slate-100 align-middle"
                    >
                      <td className="px-3 py-3 min-w-[240px]">
                        <DrugCombobox
                          value={item?.drugsId ?? null}
                          className="w-full"
                          onChange={(val) => {
                            setForm((prev) => {
                              const next = [...prev.dosageRequests];
                              next[idx] = {
                                ...next[idx],
                                drugsId: val.value,
                              };
                              return { ...prev, dosageRequests: next };
                            });
                          }}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="number"
                          min={0}
                          name="quantity"
                          onChange={(e) =>
                            handleArrayChange(e, {
                              id: item.id,
                              name: "dosageRequests",
                            })
                          }
                          className={inputCellClass}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="number"
                          name="frequency"
                          onChange={(e) =>
                            handleArrayChange(e, {
                              id: item.id,
                              name: "dosageRequests",
                            })
                          }
                          className={inputCellClass}
                        />
                      </td>
                      <td className="px-2 py-3 min-w-[180px]">
                        <Combobox
                          options={medecinTypeOptions}
                          value={item?.drugsType ?? null}
                          placeholder="Type"
                          searchPlaceholder="Rechercher..."
                          className="w-full"
                          onChange={(val) => {
                            setForm((prev) => {
                              const next = [...prev.dosageRequests];
                              next[idx] = {
                                ...next[idx],
                                drugsType: val.value,
                              };
                              return { ...prev, dosageRequests: next };
                            });
                          }}
                        />
                      </td>
                      <td className="px-2 py-3 min-w-[180px]">
                        <Combobox
                          options={timeOfDayOptions}
                          value={item?.timeOfDays ?? null}
                          placeholder="Moment"
                          searchPlaceholder="Rechercher..."
                          className="w-full"
                          onChange={(val) => {
                            setForm((prev) => {
                              const next = [...prev.dosageRequests];
                              next[idx] = {
                                ...next[idx],
                                timeOfDays: val.value,
                              };
                              return { ...prev, dosageRequests: next };
                            });
                          }}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="number"
                          min={0}
                          name="duration"
                          onChange={(e) =>
                            handleArrayChange(e, {
                              id: item.id,
                              name: "dosageRequests",
                            })
                          }
                          className={cn(inputCellClass, "min-w-[120px]")}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          onClick={(e) => handleRemoveDrug(e, item.id)}
                          className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                          aria-label="Supprimer"
                        >
                          <i className="bx bx-trash text-lg" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Rendez-vous */}
          <section className="space-y-3">
            <div className="flex items-center">
              <h4 className="text-sm font-semibold text-slate-700">
                Prochain rendez-vous
              </h4>
              <div className="flex-1 ml-4 h-px bg-slate-100" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Date
                </span>
                <input
                  type="date"
                  name="appointmentDate"
                  value={form?.appointmentDate || ""}
                  onChange={handleChange}
                  min={todayIsoDate()}
                  className={inputClass}
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Heure
                </span>
                <input
                  type="time"
                  onChange={handleTimeChange}
                  value={form?.appointmentTime}
                  className={inputClass}
                />
              </label>
            </div>

            {isPractitioner && form?.appointmentDate && (
              <AppointmentSlotPicker
                doctorId={form.doctorId}
                date={form.appointmentDate}
                selectedTime={form.appointmentTime}
                onSelect={handleSlotSelect}
              />
            )}
          </section>

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => setOpenForm(false)}
              className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="group/btn relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 disabled:opacity-60"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
              />
              <i className="bx bx-check text-lg" />
              Clôturer la consultation
            </button>
          </div>
        </form>
      )}

      {/* Liste des consultations */}
      <div className="space-y-3">
        {filteredConsultations.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-12 text-center">
            <i className="bx bx-pulse text-4xl text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-600">
              Aucune consultation
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Les consultations passées apparaîtront ici.
            </p>
          </div>
        ) : (
          filteredConsultations.map((item) => (
            <AccordionCustom
              key={item.id}
              title={
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm ring-2 ring-white">
                    <i className="bx bx-pulse text-base" />
                  </span>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                      Consultation
                    </span>
                    <span className="text-sm font-semibold text-slate-800 truncate">
                      {formatDateOnly(item?.createdAt)}
                    </span>
                  </div>
                  {item?.doctor?.firstName && (
                    <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600">
                      <i className="bx bx-plus-medical text-sm text-emerald-600" />
                      Dr. {item.doctor.firstName} {item.doctor.lastName}
                    </span>
                  )}
                  {item?.appointmentDate && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                      <i className="bx bx-calendar text-sm" />
                      RDV {formatDateOnly(item.appointmentDate)}
                      {item?.appointmentTime ? ` · ${normalizeTimeString(item.appointmentTime) || item.appointmentTime}` : ""}
                    </span>
                  )}
                </div>
              }
            >
              {(() => {
                const groupedVision = groupVisionByType(
                  item?.glassesPrescription?.visionList
                );
                const visionRows = [
                  "Distance_vision",
                  "All_distance_vision",
                  "Near_vision",
                ]
                  .map((t) => ({
                    type: t,
                    label: VISION_TYPE_LABELS[t],
                    data: groupedVision[t],
                  }))
                  .filter((row) => isVisionRowFilled(row.data));

                const prism = item?.glassesPrescription?.prism;
                const biTemporal = item?.glassesPrescription?.biTemporal;
                const activePrismOptions = prism
                  ? [
                      { key: "mineral", label: "Minéral" },
                      { key: "organic", label: "Organique" },
                      { key: "transition", label: "Transition" },
                      { key: "transitionAr", label: "Transition + AR" },
                      {
                        key: "transitionAntiBlueLight",
                        label: "Transition + Anti-lumière bleue",
                      },
                    ].filter((o) => prism[o.key])
                  : [];
                const activeShades = biTemporal
                  ? [
                      { key: "shadeWhite", label: "Blanche" },
                      { key: "shadePgx", label: "PGx" },
                      { key: "shadePbx", label: "PBx" },
                    ].filter((o) => biTemporal[o.key])
                  : [];
                const hasGlasses =
                  visionRows.length > 0 ||
                  activePrismOptions.length > 0 ||
                  activeShades.length > 0 ||
                  biTemporal?.branchLength ||
                  biTemporal?.interpupillaryDistance ||
                  biTemporal?.doubleHearths ||
                  biTemporal?.progressive;

                return (
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 px-4 py-5 sm:px-6 bg-slate-50/40">
                    {/* Colonne 1 — synthèse clinique */}
                    <aside className="xl:col-span-1 space-y-4">
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-2">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                            <i className="bx bx-calendar-event text-base" />
                          </span>
                          <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                            Prochain RDV
                          </p>
                        </div>
                        <p className="mt-3 text-sm font-semibold text-slate-800">
                          {item?.appointmentDate
                            ? formatDateOnly(item.appointmentDate)
                            : "—"}
                        </p>
                        {item?.appointmentTime && (
                          <p className="mt-0.5 text-xs text-slate-500">
                            {normalizeTimeString(item.appointmentTime) ||
                              item.appointmentTime}
                          </p>
                        )}
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-2">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-100">
                            <i className="bx bx-message-rounded-detail text-base" />
                          </span>
                          <p className="text-[11px] uppercase tracking-[0.32em] text-amber-700 font-semibold">
                            Motif
                          </p>
                        </div>
                        <p className="mt-3 text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                          {item?.complaint || (
                            <span className="text-slate-400 italic">
                              Non renseigné
                            </span>
                          )}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-2">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                            <i className="bx bx-clipboard text-base" />
                          </span>
                          <p className="text-[11px] uppercase tracking-[0.32em] text-sky-700 font-semibold">
                            Diagnostic
                          </p>
                        </div>
                        <p className="mt-3 text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                          {item?.diagnostic || (
                            <span className="text-slate-400 italic">
                              Non renseigné
                            </span>
                          )}
                        </p>
                      </div>
                    </aside>

                    {/* Colonne 2 — examens + prescriptions */}
                    <div className="xl:col-span-2 space-y-5">
                      {/* Examens */}
                      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                          <div className="flex items-center gap-2">
                            <i className="bx bx-microscope text-lg text-emerald-600" />
                            <h5 className="text-sm font-semibold text-slate-800">
                              Examens
                            </h5>
                            {item?.actsDiagnosticList?.length ? (
                              <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] font-semibold text-emerald-700">
                                {item.actsDiagnosticList.length}
                              </span>
                            ) : null}
                          </div>
                        </header>
                        {item?.actsDiagnosticList?.length ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                  <th className="px-4 py-2.5 text-left">
                                    Acte
                                  </th>
                                  <th className="px-4 py-2.5 text-left">
                                    Œil droit
                                  </th>
                                  <th className="px-4 py-2.5 text-left">
                                    Œil gauche
                                  </th>
                                  <th className="px-4 py-2.5 text-right pr-4">
                                    Fichier
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.actsDiagnosticList.map((elem, eidx) => (
                                  <tr
                                    key={eidx}
                                    className="border-t border-slate-100 hover:bg-emerald-50/30 transition-colors"
                                  >
                                    <td className="px-4 py-2.5 font-medium text-slate-800">
                                      {elem?.acts?.label || "—"}
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-600">
                                      {elem?.resultEyeRight || (
                                        <span className="text-slate-300">—</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-600">
                                      {elem?.resultEyeLeft || (
                                        <span className="text-slate-300">—</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2.5 text-right pr-4">
                                      {elem?.resultFile ? (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            openProtectedFile(elem.resultFile, {
                                              loadingMessage:
                                                "Téléchargement du fichier…",
                                            })
                                          }
                                          className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
                                        >
                                          <i className="bx bx-download text-base" />
                                          Voir
                                        </button>
                                      ) : (
                                        <span className="text-slate-300 text-xs">
                                          —
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="px-4 py-6 text-center text-xs text-slate-400">
                            Aucun examen enregistré.
                          </p>
                        )}
                      </section>

                      {/* Ordonnance médicale */}
                      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                          <div className="flex items-center gap-2">
                            <i className="bx bx-capsule text-lg text-emerald-600" />
                            <h5 className="text-sm font-semibold text-slate-800">
                              Ordonnance médicale
                            </h5>
                            {item?.medicalPrescription?.dosageList?.length ? (
                              <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] font-semibold text-emerald-700">
                                {item.medicalPrescription.dosageList.length}
                              </span>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (!item?.id) return;
                              openProtectedFile(
                                `/consultation/prescription-card-pdf/${item.id}`,
                                {
                                  loadingMessage:
                                    "Téléchargement de l'ordonnance…",
                                }
                              );
                            }}
                            disabled={!item?.id}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <i className="bx bx-download text-base" />
                            PDF
                          </button>
                        </header>
                        {item?.medicalPrescription?.dosageList?.length ? (
                          <ul className="divide-y divide-slate-100">
                            {item.medicalPrescription.dosageList.map(
                              (ord, oidx) => {
                                const drugType =
                                  DRUG_TYPE_LABELS[ord?.drugsType] ||
                                  ord?.drugsType;
                                const metaParts = [];
                                if (ord?.frequency) {
                                  metaParts.push(`${ord.frequency}×/j`);
                                }
                                if (ord?.timeOfDays) {
                                  metaParts.push(ord.timeOfDays);
                                }
                                if (ord?.duration) {
                                  metaParts.push(
                                    `${ord.duration} jour${
                                      Number(ord.duration) > 1 ? "s" : ""
                                    }`
                                  );
                                }
                                return (
                                  <li
                                    key={oidx}
                                    className="flex items-start gap-3 px-4 py-3 hover:bg-emerald-50/30 transition-colors"
                                  >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                                      <i className="bx bx-capsule text-base" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-slate-800 truncate">
                                          {ord?.drugs?.name || "Médicament"}
                                        </span>
                                        {drugType && (
                                          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                            {drugType}
                                          </span>
                                        )}
                                        {ord?.quantity ? (
                                          <span className="inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                                            Qté {ord.quantity}
                                          </span>
                                        ) : null}
                                      </div>
                                      {metaParts.length > 0 && (
                                        <p className="mt-1 text-xs text-slate-500">
                                          {metaParts.join(" · ")}
                                        </p>
                                      )}
                                    </div>
                                  </li>
                                );
                              }
                            )}
                          </ul>
                        ) : (
                          <p className="px-4 py-6 text-center text-xs text-slate-400">
                            Aucune prescription.
                          </p>
                        )}
                      </section>

                      {/* Ordonnance des lunettes */}
                      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                          <div className="flex items-center gap-2">
                            <i className="bx bx-show text-lg text-emerald-600" />
                            <h5 className="text-sm font-semibold text-slate-800">
                              Ordonnance des lunettes
                            </h5>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (!item?.id) return;
                              openProtectedFile(
                                `/consultation/glasses-prescription-card-pdf/${item.id}`,
                                {
                                  loadingMessage:
                                    "Téléchargement de l'ordonnance…",
                                }
                              );
                            }}
                            disabled={!item?.id}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <i className="bx bx-download text-base" />
                            PDF
                          </button>
                        </header>

                        {hasGlasses ? (
                          <div className="p-4 space-y-4">
                            {/* Vision grid */}
                            {visionRows.length > 0 && (
                              <div className="overflow-x-auto rounded-xl border border-slate-200">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                      <th className="px-3 py-2.5 text-left"></th>
                                      <th
                                        colSpan={3}
                                        className="px-3 py-2.5 text-center border-l border-slate-200"
                                      >
                                        OD
                                      </th>
                                      <th
                                        colSpan={3}
                                        className="px-3 py-2.5 text-center border-l border-slate-200"
                                      >
                                        OG
                                      </th>
                                    </tr>
                                    <tr className="bg-white text-[10px] text-slate-400 font-medium border-t border-slate-100">
                                      <th className="px-3 py-1.5 text-left"></th>
                                      <th className="px-2 py-1.5 text-center font-medium border-l border-slate-200">
                                        Sphère
                                      </th>
                                      <th className="px-2 py-1.5 text-center font-medium">
                                        Cylindre
                                      </th>
                                      <th className="px-2 py-1.5 text-center font-medium">
                                        Axe
                                      </th>
                                      <th className="px-2 py-1.5 text-center font-medium border-l border-slate-200">
                                        Sphère
                                      </th>
                                      <th className="px-2 py-1.5 text-center font-medium">
                                        Cylindre
                                      </th>
                                      <th className="px-2 py-1.5 text-center font-medium">
                                        Axe
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {visionRows.map((row) => (
                                      <tr
                                        key={row.type}
                                        className="border-t border-slate-100"
                                      >
                                        <th
                                          scope="row"
                                          className="px-3 py-2 text-left text-xs font-medium text-slate-700 whitespace-nowrap"
                                        >
                                          {row.label}
                                        </th>
                                        {["EYE_RIGHT", "EYE_LEFT"].flatMap(
                                          (eye, eIdx) =>
                                            ["sphere", "cylinder", "axis"].map(
                                              (field, fIdx) => {
                                                const v =
                                                  row.data?.[eye]?.[field];
                                                return (
                                                  <td
                                                    key={`${eye}-${field}`}
                                                    className={cn(
                                                      "px-2 py-2 text-center text-sm tabular-nums",
                                                      fIdx === 0 &&
                                                        "border-l border-slate-200",
                                                      v
                                                        ? "font-semibold text-slate-800"
                                                        : "text-slate-300"
                                                    )}
                                                  >
                                                    {v || "—"}
                                                  </td>
                                                );
                                              }
                                            )
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Bi-temporal + Prisme */}
                            {(biTemporal || activePrismOptions.length > 0) && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {biTemporal && (
                                  <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3.5">
                                    <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold">
                                      Bi-temporale
                                    </p>
                                    <dl className="mt-2.5 space-y-1.5 text-xs">
                                      {biTemporal.branchLength && (
                                        <div className="flex items-center justify-between gap-2">
                                          <dt className="text-slate-500">
                                            Longueur des branches
                                          </dt>
                                          <dd className="font-semibold text-slate-800 tabular-nums">
                                            {biTemporal.branchLength}
                                          </dd>
                                        </div>
                                      )}
                                      {biTemporal.interpupillaryDistance && (
                                        <div className="flex items-center justify-between gap-2">
                                          <dt className="text-slate-500">
                                            Distance interpupillaire
                                          </dt>
                                          <dd className="font-semibold text-slate-800 tabular-nums">
                                            {biTemporal.interpupillaryDistance}
                                          </dd>
                                        </div>
                                      )}
                                      {activeShades.length > 0 && (
                                        <div className="flex items-center justify-between gap-2">
                                          <dt className="text-slate-500">
                                            Teinte
                                          </dt>
                                          <dd className="flex flex-wrap gap-1 justify-end">
                                            {activeShades.map((s) => (
                                              <span
                                                key={s.key}
                                                className="inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700"
                                              >
                                                {s.label}
                                              </span>
                                            ))}
                                          </dd>
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between gap-2">
                                        <dt className="text-slate-500">
                                          Double foyers
                                        </dt>
                                        <dd
                                          className={cn(
                                            "font-medium",
                                            biTemporal.doubleHearths
                                              ? "text-emerald-700"
                                              : "text-slate-400"
                                          )}
                                        >
                                          {biTemporal.doubleHearths ? (
                                            <i className="bx bx-check-circle text-base" />
                                          ) : (
                                            <i className="bx bx-x-circle text-base" />
                                          )}
                                        </dd>
                                      </div>
                                      <div className="flex items-center justify-between gap-2">
                                        <dt className="text-slate-500">
                                          Progressifs
                                        </dt>
                                        <dd
                                          className={cn(
                                            "font-medium",
                                            biTemporal.progressive
                                              ? "text-emerald-700"
                                              : "text-slate-400"
                                          )}
                                        >
                                          {biTemporal.progressive ? (
                                            <i className="bx bx-check-circle text-base" />
                                          ) : (
                                            <i className="bx bx-x-circle text-base" />
                                          )}
                                        </dd>
                                      </div>
                                    </dl>
                                  </div>
                                )}

                                <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3.5">
                                  <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold">
                                    Prisme
                                  </p>
                                  {activePrismOptions.length > 0 ? (
                                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                                      {activePrismOptions.map((p) => (
                                        <span
                                          key={p.key}
                                          className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700"
                                        >
                                          <i className="bx bx-check text-sm" />
                                          {p.label}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="mt-2.5 text-xs text-slate-400 italic">
                                      Aucune option sélectionnée
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="px-4 py-6 text-center text-xs text-slate-400">
                            Aucune prescription de lunettes.
                          </p>
                        )}
                      </section>
                    </div>
                  </div>
                );
              })()}
            </AccordionCustom>
          ))
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({
  usersReducer,
  diseasesReducer,
  consultationsReducer,
}) => ({
  current_patient: usersReducer.current_patient,
  users: usersReducer.users,
  diseases: diseasesReducer.diseases,
  consultations: consultationsReducer.consultations,
  creating_consultation: consultationsReducer.creating_consultation,
  errors: consultationsReducer.errors,
});

export default connect(mapStateToProps)(TabTwo);
